import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { ProviderConsumer } from '@fluentui/react-northstar';
import i18next from 'i18next';
import { toast } from 'react-toastify';
import { platformService } from '../../services/platform.service';
import { getCachedFeature } from '../../cache/FeatureCache';
import { ThemeColorScheme } from '../../common/TeamsTheme';
import { filterPlatformUsersInRole, getDropdownBoxShadow } from '../../../tikit/ticketHelper';
import { LICENSE_FEATURE, toastDefault } from '../../utils/constants';
import { EMPTY_CELL } from './emptyCell';
import { isGroupMissing, sameTeamsTeam, teamHasGroups } from '../../utils/teamGroupRule';
import { CELL_BOX, InlineEditRefused, PANEL_PORTAL, SaveMark, SaveOutcome, isSaveTimeout, markClass, markLastEdited, marksOpeningCell, reportFailure, savingCell, useSaveMark, focusCellBack } from './inlineCell';
import { reloadAfterSave } from './InlineFieldCells';
import { CategoryOption } from './inlineCategoryOptions';
import { isInTypeaheadList, usePanelPlacement } from './TypeaheadList';
import { MultiTypeaheadField, TypeaheadField } from './TypeaheadField';
import { PanelActions, focusActions, holdOutsideClick, isPanelAction, wrapActionTab } from './PanelActions';

interface NamedUser { Id: number; FullName?: string; }
interface TeamRecord { Id: number; FriendlyName?: string; TeamsAadObjectId?: string; TeamsChannelId?: string; Users?: NamedUser[]; }
interface GroupRecord { Id: number; Name?: string; TeamsAadObjectId?: string; TeamsChannelId?: string; Members?: NamedUser[]; }

interface Props {
  ticket: {
    Id?: number;
    TeamId?: number | null;
    SupportGroupId?: number | null;
    AssigneeId?: number | null;
    TicketCollaborators?: { PlatformUserId: number }[];
  };
  /** The Team, Group or Assignee cell as the list draws it today. */
  resting: React.ReactElement;
  teams: TeamRecord[];
  groups: GroupRecord[];
  /** Everyone who can be assigned (agents, analysts, administrators). */
  usersInRole: NamedUser[];
  /** Teams are licensed (MultiDepartment): the Team field shows. */
  useTeam: boolean;
  /** A ticket's team is required where teams are licensed; a task's is not (its dialog does not ask for one). */
  teamRequired?: boolean;
  /**
   * A new Team brings its default channel group, as on the ticket page. A task's does not - the task dialog leaves
   * Group empty and only narrows its list to the team's groups (Shaun, 2026-10-13).
   */
  defaultGroupOnTeam?: boolean;
  onSaved: () => void | Promise<void>;
  /**
   * Puts the saved routing and collaborators into the row at once, so the cell shows them and goes green without
   * waiting for the reload. Without it (My Work) the green waits for the reload.
   */
  onPatch?: (patch: Record<string, unknown>) => void;
  /**
   * Saves somewhere other than the ticket - a task row on My Work saves to its task. Given the panel's values
   * and what changed; throws to report a failure (or a refusal, such as a closed ticket).
   */
  save?: (draft: AssignmentDraft, changed: { routing: boolean; collaborators: boolean }) => Promise<void>;
  /** Runs before anything is saved, and throws InlineEditRefused to stop it - a ticket closed since the list loaded. */
  guard?: () => Promise<void>;
  /** Which of the row's three routing cells this is - Assignee also holds the collaborators. See useLinkedSave. */
  column: AssignmentColumn;
}

export type AssignmentColumn = 'team' | 'group' | 'assignee';

interface LinkedState { busy: boolean; mark: SaveMark }
type LinkListener = ((columns: AssignmentColumn[], state: LinkedState) => void) & { column: AssignmentColumn };
const linkListeners = new Map<string, Set<LinkListener>>();

/**
 * Team, Group and Assignee are three cells opening one panel, so a save from one can change the others' values
 * (a new Team clears Group and Assignee). The cell it was made from tells the row's other cells whose values it
 * changed; those pulse and lock with it, and take its green or red (Shaun, 2026-10-14). Keyed by the ticket (or
 * task), so any list showing that row follows.
 */
const useLinkedSave = (key: number, column: AssignmentColumn, mirror: (mark: SaveMark) => void) => {
  const [linkedBusy, setLinkedBusy] = useState(false);
  const mirrorRef = useRef(mirror);
  mirrorRef.current = mirror;
  useEffect(() => {
    setLinkedBusy(false);
    const id = String(key);
    const listener: LinkListener = Object.assign((columns: AssignmentColumn[], state: LinkedState) => {
      if (!columns.includes(column)) return;
      setLinkedBusy(state.busy);
      if (!state.busy) mirrorRef.current(state.mark);
    }, { column });
    const set = linkListeners.get(id) ?? new Set<LinkListener>();
    set.add(listener);
    linkListeners.set(id, set);
    return () => { set.delete(listener); if (!set.size) linkListeners.delete(id); };
  }, [key, column]);
  const publish = (columns: AssignmentColumn[], state: LinkedState) =>
    linkListeners.get(String(key))?.forEach(listener => listener(columns, state));
  /** Whether this row's cell for that column is on screen - a view can leave Group, say, out. */
  const isShown = (other: AssignmentColumn) => [...(linkListeners.get(String(key)) ?? [])].some(listener => listener.column === other);
  return { linkedBusy, publish, isShown };
};

export interface AssignmentDraft { teamId: number | null; groupId: number | null; assigneeId: number | null; collaboratorIds: number[]; }
type Draft = AssignmentDraft;

export { InlineEditRefused };

const PANEL_WIDTH = 340;
/** Values in the row's 12px, labels at 14px like the column headers (Shaun, 2026-10-16). */
const PANEL_TYPE = 12;

const toOptions = <T extends { Id: number }>(records: T[], name: (record: T) => string | undefined): CategoryOption[] =>
  (records ?? []).map(record => ({ id: record.Id, name: name(record) ?? '' })).filter(option => option.name);

const sameIds = (a: number[], b: number[]) => [...a].sort((x, y) => x - y).join(',') === [...b].sort((x, y) => x - y).join(',');

/**
 * Team, Group, Assignee and Collaborators, edited together in a panel under whichever of those cells was
 * clicked - they decide one another, so they are one edit. The lists and the knock-on resets are the ticket
 * page's (EditTicketForm onChangeTeam / onChangeGroup / onChangeAssignee); the typing is the list cells'.
 * Saves on Save, or Enter with no list showing; Save lights only when something changed and Team, where
 * required, is set. Cancel, or Escape with no list showing, puts everything back. A click away closes it only
 * while nothing has changed.
 */
export const InlineAssignmentCell = ({ ticket, resting, teams, groups, usersInRole, useTeam, teamRequired = true, defaultGroupOnTeam = true, onSaved, onPatch, save: saveElsewhere, guard, column }: Props) => {
  const original: Draft = useMemo(() => ({
    teamId: ticket.TeamId ?? null,
    groupId: ticket.SupportGroupId ?? null,
    assigneeId: ticket.AssigneeId ?? null,
    collaboratorIds: (ticket.TicketCollaborators ?? []).map(c => c.PlatformUserId)
  }), [ticket.Id, ticket.TeamId, ticket.SupportGroupId, ticket.AssigneeId, (ticket.TicketCollaborators ?? []).map(c => c.PlatformUserId).join(',')]);

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<Draft>(original);
  const [collaboratorsOn, setCollaboratorsOn] = useState(false);

  const cellRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Follows the cell as the grid scrolls either way, and stays within the view's left and right.
  const position = usePanelPlacement(cellRef, panelRef, open, PANEL_WIDTH, 320);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const closingRef = useRef(false);
  const saveMark = useSaveMark(ticket.Id, [original.teamId, original.groupId, original.assigneeId, original.collaboratorIds.join(',')].join('|'), saving, column);

  useEffect(() => {
    getCachedFeature(LICENSE_FEATURE.Collaborators).then(setCollaboratorsOn);
  }, []);

  // The panel is portalled to the end of the page, so focus left on the cell would Tab down the grid to the
  // pager. It takes focus once it is placed; its first Tab then goes into the first field (onPanelKeyDown).
  useEffect(() => { if (open && position) panelRef.current?.focus({ preventScroll: true }); }, [open, position != null]);

  // A refetch can hand this row's cell another ticket: stop looking busy. New values for this ticket - put in the
  // row by its own save as it starts, or by a reload - only close it.
  useEffect(() => { setSaving(false); }, [ticket.Id]);

  const linked = useLinkedSave(ticket.Id, column, saveMark.mirror);
  /** The row's other cells this save changes, told how it goes until it ends. */
  const linkedColumnsRef = useRef<AssignmentColumn[] | null>(null);
  /** The outcome for those cells when this one does not show it (its own value did not change). */
  const linkedMarkRef = useRef<SaveMark>(null);
  useEffect(() => {
    const columns = linkedColumnsRef.current;
    if (!columns) return;
    linked.publish(columns, { busy: saveMark.busy, mark: linkedMarkRef.current ?? saveMark.mark });
    if (!saveMark.busy) linkedColumnsRef.current = null;
  }, [saveMark.busy, saveMark.mark]);
  useEffect(() => { setOpen(false); }, [original]);

  const team = teams.find(t => t.Id === draft.teamId);
  const group = groups.find(g => g.Id === draft.groupId);

  const teamOptions = useMemo(() => toOptions(teams, t => t.FriendlyName), [teams]);
  const groupOptions = useMemo(() => toOptions(
    useTeam && team ? groups.filter(g => sameTeamsTeam(g, team)) : groups, g => g.Name
  ), [groups, team, useTeam]);
  const assigneeOptions = useMemo(() => {
    let pool: unknown[] = usersInRole;
    if (group) pool = group.Members ?? [];
    else if (team) pool = team.Users ?? [];
    return toOptions(filterPlatformUsersInRole(pool as PlatformUser[], usersInRole) as NamedUser[], u => u.FullName)
      .filter(option => !draft.collaboratorIds.includes(option.id));
  }, [group, team, usersInRole, draft.collaboratorIds]);
  const collaboratorOptions = useMemo(
    () => toOptions(usersInRole, u => u.FullName).filter(option => option.id !== draft.assigneeId),
    [usersInRole, draft.assigneeId]
  );

  // As on the ticket page: a new team brings its default channel group and clears the assignee; a new group
  // clears the assignee.
  const setTeam = (teamId: number | null) => {
    if (teamId === draftRef.current.teamId) return;
    const next = teams.find(t => t.Id === teamId);
    const defaultGroup = next && defaultGroupOnTeam ? groups.find(g => sameTeamsTeam(g, next) && g.TeamsChannelId === next.TeamsChannelId) : undefined;
    setDraft(d => ({ ...d, teamId, groupId: defaultGroup?.Id ?? null, assigneeId: null }));
  };
  const setGroup = (groupId: number | null) => {
    if (groupId === draftRef.current.groupId) return;
    setDraft(d => ({ ...d, groupId, assigneeId: null }));
  };
  const setAssignee = (assigneeId: number | null) => setDraft(d => ({ ...d, assigneeId }));
  const setCollaborators = (collaboratorIds: number[]) => setDraft(d => ({ ...d, collaboratorIds }));


  const routingChanged = (d: Draft) =>
    d.teamId !== original.teamId || d.groupId !== original.groupId || d.assigneeId !== original.assigneeId;
  const collaboratorsDiffer = (d: Draft) => collaboratorsOn && !sameIds(d.collaboratorIds, original.collaboratorIds);
  // Team is required where teams are on, and so is Group when that team has groups - the ticket page's rule, which
  // holds for a ticket saved before it too (#33693). A task's routing needs neither. Save stays off until both are met.
  const rulesApply = useTeam && teamRequired;
  const groupRequired = (d: Draft) => rulesApply && teamHasGroups(teams.find(team => team.Id === d.teamId), groups);
  const routingValid = (d: Draft) => !rulesApply || (d.teamId != null && !isGroupMissing(d.teamId, d.groupId, teams, groups));
  const dirty = open && (routingChanged(draft) || collaboratorsDiffer(draft));
  const canSave = dirty && routingValid(draft);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  const save = async () => {
    const d = draftRef.current;
    const routingOk = routingValid(d);
    const patch: Record<string, number | null> = {};
    if (routingOk) {
      if (d.teamId !== original.teamId) patch.TeamId = d.teamId;
      if (d.groupId !== original.groupId) patch.SupportGroupId = d.groupId;
      if (d.assigneeId !== original.assigneeId) patch.AssigneeId = d.assigneeId;
    }
    const collaboratorsChanged = collaboratorsDiffer(d);
    const routingSent = Object.keys(patch).length > 0;
    if (!routingSent && !collaboratorsChanged) return;

    // The names and people the list's own renderers read (toTicketRow, _getAssignee), for the parts being saved.
    const rowFields = (x: Draft) => {
      const user = (id: number | null) => usersInRole.find(u => u.Id === id) ?? null;
      return {
        ...(routingSent ? {
          TeamId: x.teamId, SupportGroupId: x.groupId, AssigneeId: x.assigneeId,
          TeamName: teams.find(team => team.Id === x.teamId)?.FriendlyName ?? EMPTY_CELL,
          GroupName: groups.find(g => g.Id === x.groupId)?.Name ?? EMPTY_CELL,
          Assignee: user(x.assigneeId),
          AssigneeName: user(x.assigneeId)?.FullName ?? i18next.t('ticket.ticket-board.ticket-card.unassigned')
        } : {}),
        ...(collaboratorsChanged ? { TicketCollaborators: x.collaboratorIds.map(id => ({ PlatformUserId: id, PlatformUser: user(id) })) } : {})
      };
    };

    const changedColumns: AssignmentColumn[] = [
      ...(routingSent && d.teamId !== original.teamId ? ['team' as const] : []),
      ...(routingSent && d.groupId !== original.groupId ? ['group' as const] : []),
      ...((routingSent && d.assigneeId !== original.assigneeId) || collaboratorsChanged ? ['assignee' as const] : [])
    ];
    linkedColumnsRef.current = changedColumns.filter(other => other !== column);
    linkedMarkRef.current = null;
    // Green goes on the cells whose value changed, whichever of the three the panel was opened from - and on this
    // one only when it changed too, or when no changed cell is in the view (Group left out of it, say), so every
    // save shows its outcome somewhere (Shaun, 2026-10-15). A failure always marks this cell, where the panel reopens.
    const markHere = marksOpeningCell(changedColumns, column, linked.isShown);

    saveMark.begin();
    setSaving(true);
    // The row shows the new values while the save is out, drawn by the list's own renderers - and goes back to
    // the ticket's own if the save does not land.
    onPatch?.(rowFields(d));
    /** Set once the routing is saved: a failure after it is the collaborators alone. */
    let routingSaved = false;
    try {
      await guard?.();
      if (saveElsewhere) {
        // Routing that breaks the rules above (no team, or no group for a team with groups) keeps the saved routing.
        const kept = routingOk ? d : { ...d, teamId: original.teamId, groupId: original.groupId, assigneeId: original.assigneeId };
        await saveElsewhere(kept, { routing: Object.keys(patch).length > 0, collaborators: collaboratorsChanged });
      } else {
        const api = new platformService();
        if (Object.keys(patch).length) { await api.updateTicket(ticket.Id, patch); routingSaved = true; }
        if (collaboratorsChanged) await api.UpdateCollaborators(ticket.Id, d.collaboratorIds);
      }
      if (onPatch) reloadAfterSave(onSaved);
      else await onSaved();
      linkedMarkRef.current = 'saved';
      // The box that stays goes on the value that changed: this cell if it changed (or none in view did), otherwise
      // the first changed cell in the view - it takes this save's green (useLinkedSave) and settles into the box.
      if (markHere) saveMark.show('saved');
      else {
        const target = changedColumns.find(other => other !== column && linked.isShown(other));
        if (target) markLastEdited(target, ticket.Id);
      }
      setSaving(false);
    } catch (error: unknown) {
      onPatch?.(rowFields(original));
      let outcome: SaveOutcome;
      if (routingSaved && !isSaveTimeout(error)) {
        console.error('Inline cell save failed', error);
        toast.error(t('ticket-details.edit-ticket-view.failed-to-update-the-collaborators'), toastDefault);
        outcome = 'failed';
      } else {
        outcome = reportFailure(error, {});
      }
      saveMark.show(outcome);
      setSaving(false);
      // The row is fetched again whatever failed, so routing saved before the collaborators failed shows. The panel
      // then reopens with the agent's picks, on the saved routing - so only the collaborators are left to save.
      await Promise.resolve(onSaved()).catch(() => undefined);
      if (outcome === 'failed') reopenWithDraft();
    }
  };

  const reopenWithDraft = () => {
    closingRef.current = false;
    setOpen(true);
  };

  const openPanel = () => {
    saveMark.opening();
    closingRef.current = false;
    setDraft(original);
    setOpen(true);
  };

  const close = (keep: boolean, refocus: boolean) => {
    if (closingRef.current) return;
    closingRef.current = true;
    setOpen(false);
    if (keep) save();
    if (refocus) focusCellBack(cellRef.current);
  };

  // A click outside the cell, the panel and the panel's suggestion lists closes it - unless something has
  // changed, which only Save, Cancel or Escape settle.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (event: MouseEvent) => {
      const path = event.composedPath();
      if (path.includes(panelRef.current) || path.includes(cellRef.current) || isInTypeaheadList(path)) return;
      if (dirtyRef.current) holdOutsideClick(event, panelRef.current, cellRef.current);
      else close(false, false);
    };
    document.addEventListener('mousedown', onMouseDown, true);
    return () => document.removeEventListener('mousedown', onMouseDown, true);
  }, [open]);

  // Reached only when a field did not use the key itself (no list was showing).
  const onPanelKeyDown = (event: React.KeyboardEvent) => {
    event.stopPropagation();
    if (event.key === 'Escape') { event.preventDefault(); close(false, true); return; }
    const inputs = Array.from(panelRef.current?.querySelectorAll('input') ?? []);
    if (isPanelAction(event.target)) { wrapActionTab(event, panelRef.current, inputs); return; }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (canSave) close(true, true);
      else if (!dirty) close(false, true);
      return;
    }
    if (event.key === 'Tab' && event.target === panelRef.current) {
      event.preventDefault();
      (event.shiftKey ? inputs[inputs.length - 1] : inputs[0])?.focus();
      return;
    }
    if (event.key === 'Tab') {
      const edge = event.shiftKey ? inputs[0] : inputs[inputs.length - 1];
      if (event.target === edge) {
        // Let the field take its lit option first; it does that on this same keydown.
        event.preventDefault();
        const backward = event.shiftKey;
        setTimeout(() => focusActions(panelRef.current, backward), 0);
      }
    }
  };

  const stop = (event: React.SyntheticEvent) => event.stopPropagation();
  const t = (key: string) => i18next.t(key);

  return (
    <ProviderConsumer
      render={globalTheme => {
        const marked = markClass(saveMark.mark, saveMark.edited);
        const lock = savingCell(saveMark.busy || linked.linkedBusy);
        return (
        <>
          <div
            ref={cellRef}
            className={`inline-edit-cell${marked}${lock.className}`}
            {...lock.props}
            data-selection-disabled={true}
            tabIndex={0}
            role="button"
            aria-expanded={open}
            aria-label={t('ticket.ticket-list.assignee')}
            onMouseDown={stop}
            onClick={event => { stop(event); if (!open) openPanel(); }}
            onKeyDown={event => {
              if (event.key === 'Escape' && open) { stop(event); event.preventDefault(); close(false, true); return; }
              if (!open && (event.key === 'Enter' || event.key === 'F2')) { stop(event); event.preventDefault(); openPanel(); }
            }}
            style={{
              ...CELL_BOX,
              
              cursor: 'text',
              outline: open ? `1px solid ${globalTheme.siteVariables.colorScheme.brand.foreground}` : undefined,
              outlineOffset: -1, ...saveMark.markStyle
            }}
          >
            {resting}
          </div>
          {open && position && ReactDOM.createPortal(
            <div
              ref={panelRef}
              {...PANEL_PORTAL}
              role="dialog"
              tabIndex={-1}
              aria-label={t('ticket.ticket-list.assignee')}
              className="rounded"
              onMouseDown={stop}
              onClick={stop}
              onDoubleClick={stop}
              onKeyDown={onPanelKeyDown}
              style={{
                position: 'fixed',
                left: position.left,
                ...(position.bottom === undefined ? { top: position.top } : { bottom: position.bottom }),
                width: PANEL_WIDTH,
                zIndex: 1000000,
                outline: 'none',
                padding: 12,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                backgroundColor: globalTheme.siteVariables.colorScheme.default.background,
                // The panel is portalled to the page's body, outside the themed app, so its text takes the theme's colour here.
                color: globalTheme.siteVariables.colorScheme.default.foreground,
                boxShadow: getDropdownBoxShadow(globalTheme),
                ...ThemeColorScheme(globalTheme.siteVariables)
              }}
            >
              {useTeam && (
                <TypeaheadField
                  label={t('ticket.add-ticket.team')}
                  options={teamOptions}
                  valueId={draft.teamId}
                  onChange={setTeam}
                  required={teamRequired}
                  size={PANEL_TYPE}
                  clearable
                />
              )}
              <TypeaheadField
                label={t('ticket.add-ticket.group')}
                options={groupOptions}
                valueId={draft.groupId}
                onChange={setGroup}
                required={groupRequired(draft)}
                size={PANEL_TYPE}
                clearable
              />
              <TypeaheadField
                label={t('ticket.add-ticket.assignee')}
                options={assigneeOptions}
                valueId={draft.assigneeId}
                onChange={setAssignee}
                placeholder={t('ticket.ticket-board.ticket-card.unassigned')}
                size={PANEL_TYPE}
                clearable
              />
              {collaboratorsOn && (
                <MultiTypeaheadField
                  label={t('ticket.ticket-list.collaborators')}
                  options={collaboratorOptions}
                  valueIds={draft.collaboratorIds}
                  onChange={setCollaborators}
                  placeholder={t('ticket.add-ticket.collaborators')}
                  size={PANEL_TYPE}
                />
              )}
              <PanelActions canSave={canSave} onSave={() => close(true, true)} onCancel={() => close(false, true)} />
            </div>,
            document.body
          )}
        </>
        );
      }}
    />
  );
};
